import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useParams, useHistory } from 'react-router-dom'

import Loading from '@/shared/Loading'
import Card from '@/shared/Card'

import PrimaryButton from '@/shared/Buttons/Primary'
import UserCreditRow from '@/pages/Credits/UserCreditRow'
import CreditTabs from '@/pages/Credits/Tabs'
import useQuery from '@/hooks/useQuery'
import DefaultButton from '@/shared/Buttons/Default'
import Modal from '@/shared/Modal'
import useModal from '@/hooks/useModalV2'
import { useGlobalState } from '@/state'

const TransferOwnership = ({ userCredits, credit }) => {
  const [currentUser] = useGlobalState('currentUser')
  const { putpostRequest } = useQuery()
  const history = useHistory()
  const { openModal, closeModal, isOpen } = useModal()
  const [userId, setUserId] = useState(currentUser.id)
  const [loading, setLoading] = useState(false)
  const [, setToast] = useGlobalState('toast')

  if (currentUser.id !== credit.userId) { return null }

  const save = () => {
    if (String(userId) === String(currentUser.id)) {
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-white text-gray-800">Nobody Was Transferred</p>
        <p className="mt-1 text-sm dark:text-gray-100 text-gray-500">You picked yourself. You already own this one, boss.</p>
      </div>)
      return
    }

    openModal()
  }

  const transfer = () => {
    setLoading(true)
    putpostRequest(`/api/v3/credits/${credit.id}`, 'PATCH', { credit: { transfer_user_id: userId } }, (err, jsonData) => {
      setLoading(false)
      if (err) { // 422 code
        if (typeof err !== 'string') {
          Object.entries(err).forEach(([key, value]) => {
            console.log(key, value)
          })
        }
        closeModal()
        return
      }

      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-white text-gray-800">Ownership Transferred</p>
        <p className="mt-1 text-sm dark:text-gray-100 text-gray-500">They are the captain now.</p>
      </div>)
      history.push(`/credits/${credit.slug}`)
    })
  }

  const handleChange = (e) => {
    if (e.target.name === 'credit_user_id') {
      setUserId(e.target.value)
    }
  }

  const eligibleUCs = userCredits.filter(uc => uc.status === 'confirmed' && uc.userId && uc.userId !== currentUser.id)
  const newOwner = eligibleUCs.find(uc => String(uc.userId) === String(userId))

  // Ownership is the only thing keeping most owners on the team. If you don't
  // also hold an Edit-level credit, handing the keys over locks you out.
  const myCredit = userCredits.find(uc => uc.userId === currentUser.id && uc.status === 'confirmed')
  const keepsAccess = myCredit?.permissions === 'edit'

  return <>
  <Card title="Transfer Ownership" footer={
    <div className='flex flex-rows justify-end items-end'>
      <div className='flex flex-col items-end w-min ml-2'>
        <div className="hidden sm:block w-min text-xs border border-gray-200 dark:border-gray-800 rounded px-2 font-sans text-gray-500 dark:text-gray-400">⌘/ctrl+ENTER</div>
        <PrimaryButton className='w-full' onClick={save} loading={loading} text='Save' />
      </div>
    </div>
    } >
    <span className='text-xs sm:text-sm dark:text-gray-300 text-gray-700'>If you would like to transfer ownership of this production to someone else, make sure they are a Credited Member, and then they will appear in this dropdown. Please note that the owner must confirm their Credited Member status before they appear in this dropdown.</span>
    <div className='col-span-1'>
      <div className="col-span-6 sm:col-span-3">
        <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-200 flex justify-between">
          <span>Owner</span>
        </label>
        <select value={userId} onChange={handleChange} name='credit_user_id' className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-cccpurple focus:border-cccpurple sm:text-sm" >
          <option value={currentUser.id}>{currentUser.username}</option>
          {eligibleUCs.map((userCredit, idx) => <option key={`elig${userCredit.id}`} value={userCredit.userId}>{userCredit.username}</option>)}
        </select>
      </div>
    </div>
  </Card>
  <Modal isOpen={isOpen} closeModal={closeModal} title="Hand Over The Keys?">
    <div className='mt-4 space-y-3 text-slate-900 dark:text-slate-100'>
      <p className='text-sm sm:text-base'>
        You are about to make <b>{newOwner?.username}</b> the owner of <b>{credit.name}</b>. They get the team, the production, and the big red Delete button. You do not.
      </p>
      { !keepsAccess && <div className='rounded-md border border-red-300 bg-red-50 dark:bg-red-900/40 dark:border-red-700 p-3'>
        <p className='text-sm font-medium text-red-800 dark:text-red-200'>You are about to lock yourself out.</p>
        <p className='mt-1 text-sm text-red-700 dark:text-red-200'>
          You are not a Credited Member with Edit permissions on this production, so the moment you transfer it you lose access entirely. Getting back in is up to {newOwner?.username}. If that is not the plan, hit Never Mind, add yourself as a Credited Member first, and then come back.
        </p>
      </div> }
      { keepsAccess && <div className='rounded-md border border-gray-300 dark:border-gray-600 p-3'>
        <p className='text-sm text-gray-700 dark:text-gray-200'>
          Your Edit-level credit sticks around, so you can still manage the team and the production. You just will not own it anymore, and the new owner can take that access away.
        </p>
      </div> }
      <p className='text-sm text-gray-700 dark:text-gray-200'>Only {newOwner?.username} can hand it back. Sure?</p>
    </div>
    <div className='flex justify-end mt-5'>
      <PrimaryButton loading={loading} onClick={transfer} text='Yes, Transfer It' />
      <DefaultButton className='ml-5' onClick={closeModal} text='Never Mind' />
    </div>
  </Modal>
  </>
}

const CreditManage = () => {
  const { id } = useParams()
  const { getRequest } = useQuery()
  const [credit, setCredit] = useGlobalState('credit')
  const [creatingNewUserCredit, setCreatingNewUserCredit] = useState(false)
  const [userCredits, setUserCredits] = useState([])

  useEffect(() => {
    if (!creatingNewUserCredit) {
      getRequest(`/api/v3/credits/${id}/manage`, {}, (err, jsonData) => {
        if (err) { /* handled in hook */ return }
        setCredit(jsonData.credit)
        setUserCredits(jsonData.userCredits)
      })
    }
  }, [id, creatingNewUserCredit])

  if (!credit.id) { return <Loading /> }

  return <div>
    <CreditTabs credit={credit} />
    <Card title="Credited Members" footer={<DefaultButton onClick={() => setCreatingNewUserCredit(true)} text='New Credited Member' />}>
      <div className="min-w-full divide-y divide-gray-200">
        <div className='hidden sm:block'>
          <div className="bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-300 p-0 sm:p-2 flex justify-start items-center space-x-1 sm:space-x-3">
            <div className="w-64 text-xs font-medium uppercase tracking-wider"> Name </div>
            <div className="w-64 text-xs font-medium uppercase tracking-wider"> Credited For </div>
            <div className="relative px-6 py-3"> <span className="sr-only" hidden>Edit</span> </div>
          </div>
        </div>
        {userCredits.map((userCredit, idx) => (
          <UserCreditRow
            key={`ucform${userCredit.id}`}
            idx={idx}
            initialUserCredit={userCredit}
            creditId={credit.id}
            canEdit={credit.canEdit}
          />
        ))}
      </div>
      { creatingNewUserCredit && <UserCreditRow
        initialUserCredit={{}}
        creditId={credit.id}
        setCreatingNewUserCredit={setCreatingNewUserCredit}
      /> }
    </Card>

    <div className='mt-4' />
    <TransferOwnership userCredits={userCredits} credit={credit} />
  </div>
}

export default CreditManage

TransferOwnership.propTypes = {
  userCredits: PropTypes.array,
  credit: PropTypes.object
}
